import React, { useEffect, useState } from "react";
import { Loader2, Copy, Link as LinkIcon, Send, X, ShieldAlert, Ban, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

/** Modal shown from a job detail. Lets admin generate a magic-link URL for the
 * client, copy it, and optionally email it via Resend. */
export const ClientLinkModal = ({ job, onClose }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [recipient, setRecipient] = useState(job?.contact_email || "");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setLinks(await adminApi.listClientLinks(job.id)); }
    catch { toast.error("Could not load links"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);

  const create = async () => {
    setCreating(true);
    try {
      const link = await adminApi.createClientLink(job.id, { recipient_email: recipient || null });
      toast.success("Secure client link created");
      setLinks((prev) => [link, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The client will no longer be able to open it.")) return;
    try {
      await adminApi.revokeClientLink(token);
      toast.success("Revoked");
      load();
    } catch { toast.error("Could not revoke"); }
  };

  const copyUrl = async (token) => {
    const url = `${window.location.origin}/portal/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const sendEmail = async (token) => {
    if (!recipient) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const r = await adminApi.sendClientLinkEmail(token, { recipient_email: recipient });
      if (r.ok) {
        toast.success(`Email sent to ${recipient}`);
        setEmailFor(null);
        load();
      } else {
        toast.error(r.error || "Send failed — check Settings → Email");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="client-link-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-auto">
        <header className="sticky top-0 bg-white border-b border-[#cfd7db] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LinkIcon size={16} className="text-[#f28a16]" />
            <h2 className="font-display uppercase tracking-widest text-sm text-[#111619]">Client secure link</h2>
          </div>
          <button onClick={onClose} className="text-[#3f4b52] hover:text-[#111619]" data-testid="link-modal-close"><X size={18} /></button>
        </header>

        <div className="p-5 space-y-5">
          <p className="text-sm text-[#3f4b52]">
            Send the client a one-click, no-login URL to view their quote, accept it and download PDFs.
            Links expire in 30 days by default and can be revoked at any time.
          </p>

          <div className="border border-[#cfd7db] rounded-md p-4">
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-2">Generate a new link</div>
            <div className="flex flex-wrap gap-2">
              <input
                type="email"
                placeholder="Optional client email (for sending)"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                data-testid="link-recipient-input"
                className="flex-1 min-w-[220px] h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white"
              />
              <button
                onClick={create}
                disabled={creating}
                data-testid="link-create-btn"
                className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
              >
                {creating ? <Loader2 className="animate-spin" size={14} /> : <LinkIcon size={14} />} Create link
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-2">Existing links ({links.length})</div>
            {loading ? (
              <div className="text-sm text-[#3f4b52] inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
            ) : links.length === 0 ? (
              <div className="text-sm text-[#3f4b52] italic">No links yet. Create one above.</div>
            ) : (
              <ul className="space-y-2">
                {links.map((l) => {
                  const url = `${window.location.origin}/portal/${l.token}`;
                  const expired = new Date(l.expires_at) < new Date();
                  const used = !!(l.used && l.used.accept_quote);
                  return (
                    <li key={l.token} className={`border rounded-md p-3 ${l.revoked ? "border-[#dc2626]/30 bg-[#dc2626]/5" : expired ? "border-[#cfd7db] bg-[#f5f6f6] opacity-70" : "border-[#cfd7db] bg-white"}`} data-testid={`link-row-${l.token.slice(0, 8)}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-[#111619] break-all font-mono">{url}</div>
                          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mt-1">
                            {l.revoked ? <span className="text-[#dc2626]">REVOKED</span> : expired ? "EXPIRED" : "ACTIVE"}
                            {" · "}Expires {(l.expires_at || "").slice(0, 10)}
                            {used && <> · <span className="text-[#065f46]">Quote accepted</span></>}
                            {l.emailed_at && <> · Emailed {(l.emailed_at || "").slice(0, 10)}</>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <a href={url} target="_blank" rel="noreferrer"
                             className="border border-[#cfd7db] text-[#3f4b52] w-8 h-8 rounded inline-flex items-center justify-center hover:bg-[#f5f6f6]"
                             title="Open link"><ExternalLink size={14} /></a>
                          <button onClick={() => copyUrl(l.token)}
                                  data-testid={`link-copy-${l.token.slice(0, 8)}`}
                                  className="border border-[#cfd7db] text-[#3f4b52] w-8 h-8 rounded inline-flex items-center justify-center hover:bg-[#f5f6f6]"
                                  title="Copy"><Copy size={14} /></button>
                          {!l.revoked && !expired && (
                            <>
                              <button onClick={() => { setEmailFor(l.token); setRecipient(l.recipient_email || recipient); }}
                                      data-testid={`link-email-${l.token.slice(0, 8)}`}
                                      className="border border-[#f28a16] text-[#f28a16] w-8 h-8 rounded inline-flex items-center justify-center hover:bg-[#f28a16]/10"
                                      title="Email link"><Send size={14} /></button>
                              <button onClick={() => revoke(l.token)}
                                      data-testid={`link-revoke-${l.token.slice(0, 8)}`}
                                      className="border border-[#dc2626] text-[#dc2626] w-8 h-8 rounded inline-flex items-center justify-center hover:bg-[#dc2626]/10"
                                      title="Revoke"><Ban size={14} /></button>
                            </>
                          )}
                        </div>
                      </div>

                      {emailFor === l.token && (
                        <div className="mt-3 pt-3 border-t border-[#cfd7db] flex flex-wrap gap-2">
                          <input
                            type="email"
                            value={recipient}
                            onChange={(e) => setRecipient(e.target.value)}
                            placeholder="client@example.com"
                            data-testid={`link-email-recipient-${l.token.slice(0, 8)}`}
                            className="flex-1 min-w-[220px] h-9 px-3 border border-[#cfd7db] rounded-md text-sm"
                          />
                          <button
                            onClick={() => sendEmail(l.token)}
                            disabled={sending}
                            data-testid={`link-email-send-${l.token.slice(0, 8)}`}
                            className="bg-[#10b981] text-white px-3 h-9 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-1"
                          >
                            {sending ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Send
                          </button>
                          <button
                            onClick={() => setEmailFor(null)}
                            className="border border-[#cfd7db] text-[#3f4b52] px-3 h-9 rounded-md font-display uppercase tracking-widest text-xs"
                          >Cancel</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="text-xs text-[#3f4b52] flex items-start gap-2 pt-3 border-t border-[#cfd7db]">
            <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
            <div>
              To send emails you need a Resend API key configured in <b>Admin → Settings → Email (Resend)</b>. Copy-paste the link works even without email.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientLinkModal;
